/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useMemo } from 'react';
import Tooltip from 'rc-tooltip';
import { plurals, statusColors } from 'ogc-processes-components';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { AppRoutes } from '@/constants/routes.ts';
import { Utils } from '@/utils/utils.ts';
import { NavLink } from 'react-router';
import styled from 'styled-components';
import { MonitorProcessData } from '@/types/models';
import { useJobsProcessInfoData } from './JobsMonitorContext';

interface Props {
  className?: string;
  monitorData: MonitorProcessData;
}

const Bubble = styled.span`
  min-width: 34px;
  min-height: 34px;
  border-radius: 30px;
  padding: 3px;
  &:hover {
    border: solid 2px var(--text-color);
    transition: all 0.1s;
  }
`;

export const JobsRunCounter: React.FC<Props> = ({ className, monitorData }) => {
  const { t } = useTranslation();

  const { processesInfoData } = useJobsProcessInfoData();

  const rowProcessInfo = useMemo(() => {
    if (processesInfoData && monitorData) {
      const rowInfo = processesInfoData?.find(
        (el) => el.fecthStatus === 'sucessfull' && el.processId === monitorData.id
      );
      return rowInfo;
    }
  }, [processesInfoData, monitorData]);

  const redirectTo = useCallback(
    (status: keyof typeof statusColors) => {
      const url = AppRoutes.jobs.processHistory.url(monitorData.id);
      if (!rowProcessInfo) {
        return url + Utils.buildWithQueryParamsUrl({ job_status: status });
      }
      if (rowProcessInfo?.data?.link) {
        return (
          url + Utils.buildWithQueryParamsUrl({ job_status: status, job_reference: rowProcessInfo.data.project_name })
        );
      }
      return '#';
    },
    [rowProcessInfo, monitorData]
  );

  return (
    <div className={classNames(className, 'flex align-items-center justify-content-center')}>
      {Object.keys(statusColors).map((key) => {
        const value = monitorData[key as never] as number;
        const _value = value <= 999 ? value : (value / 100).toPrecision(2).concat(' k');
        return (
          <Tooltip
            key={key}
            placement={'bottom'}
            overlay={
              <div className="text-center">
                <span className="iblock">
                  {plurals(value, 'job')} {t(['jobs.properties.status.'.concat(key), key])}.
                </span>
                <span className="block font-medium">{t(['actions.click_to_see_more', 'clique to see more'])}.</span>
              </div>
            }
          >
            <NavLink to={redirectTo(key as keyof typeof statusColors)}>
              <Bubble
                key={key}
                style={{
                  backgroundColor: statusColors[key as never]
                }}
                className="mx-1 inline-flex align-items-center justify-content-center text-white font-bold"
              >
                {_value}
              </Bubble>
            </NavLink>
          </Tooltip>
        );
      })}
    </div>
  );
};
